/**
 * Route guards.
 *
 * COMMERCIAL_MODULARITY §6.2, enforcement point 2: "Route guards refuse direct
 * URL entry, deep links and browser history." Point 1 — filtering navigation —
 * is explicitly "courtesy only".
 *
 * Both call the same `authorise()` the service layer calls, through the pure
 * guard entry point. A second implementation here would be a second set of
 * rules, and the first time they disagreed the wrong one would be the one the
 * user saw.
 *
 * **This is not the enforcement.** The server refuses regardless, and the
 * generated entitlement suite proves it. A guard that stopped working would
 * produce a broken screen, not an open door.
 */
import { authorise, type AuthorisationDecision } from '@tessma/authz/guard';
import {
  moduleDefinition,
  phaseOneModules,
  sellableModules,
  PLATFORM_CORE_MODULE_KEY,
} from '@tessma/registry';
import { contextFrom, type Session, type SessionState } from './session.js';

export class NotAuthenticatedError extends Error {
  readonly returnTo: string;

  constructor(returnTo: string) {
    super('Not authenticated');
    this.name = 'NotAuthenticatedError';
    this.returnTo = returnTo;
  }
}

/**
 * A route that does not exist, as far as this actor is concerned.
 *
 * Thrown for an unentitled module *and* for one out of scope, because both
 * answer not found — telling a caller that something exists but was not
 * purchased confirms its existence (SECURITY §1.8).
 */
export class RouteNotFoundError extends Error {
  constructor() {
    super('Not found');
    this.name = 'RouteNotFoundError';
  }
}

export class RouteForbiddenError extends Error {
  readonly decision: AuthorisationDecision;

  constructor(decision: AuthorisationDecision) {
    super('Not permitted');
    this.name = 'RouteForbiddenError';
    this.decision = decision;
  }
}

export function requireAuthenticated(state: SessionState, returnTo: string): Session {
  if (!state.authenticated) throw new NotAuthenticatedError(returnTo);
  return state;
}

export interface RouteRequirement {
  readonly permission: string;
  readonly moduleKey?: string | undefined;
  readonly featureKey?: string | undefined;
}

export function guardRoute(session: Session, requirement: RouteRequirement): void {
  const decision = authorise(contextFrom(session), requirement, { stepUpSatisfied: false });
  if (decision.allowed) return;
  if (decision.response === 'not_found') throw new RouteNotFoundError();
  throw new RouteForbiddenError(decision);
}

/**
 * Is this navigation module reachable at all?
 *
 * Resolved through the registry rather than a list in the UI, so a module added
 * to the registry is filtered correctly without a change here (§3).
 *
 * TWO FILTERS, IN ORDER. `phaseOneModules()` first — a module outside the
 * approved commercial model is not reachable for anyone, however their
 * entitlements read — then the tenant's own entitlements. A stale entitlement
 * row for a module later withdrawn from the release therefore stops opening a
 * door, which is the behaviour a withdrawal is for.
 */
export function reachableNavigationModules(session: SessionState): ReadonlySet<string> {
  // Dashboard and Administration are mandatory Platform Core navigation, which
  // every tenant has and no entitlement grants.
  const reachable = new Set<string>(['dashboard']);
  const core = moduleDefinition(PLATFORM_CORE_MODULE_KEY)?.navigationModuleKey;
  if (core !== undefined) reachable.add(core);
  if (!session.authenticated) return reachable;

  for (const module of phaseOneModules()) {
    if (!session.entitlements.modules.includes(module.moduleKey)) continue;
    if (module.navigationModuleKey !== undefined) reachable.add(module.navigationModuleKey);
  }
  return reachable;
}

/**
 * Which SKU a navigation entry belongs to, or undefined for mandatory Core.
 *
 * DELIBERATELY `sellableModules()` AND NOT `phaseOneModules()`. The route guard
 * reads `undefined` as "mandatory Platform Core, let it through", so narrowing
 * this to the current release would make `/m/field-service` answer *undefined*
 * and be admitted as if it were Core. Every sellable module must be recognised
 * as a SKU here precisely so that the entitlement check below it gets to run
 * and refuse.
 */
export function skuForNavigation(navigationModuleKey: string): string | undefined {
  return sellableModules().find((module) => module.navigationModuleKey === navigationModuleKey)
    ?.moduleKey;
}
